/**
 * Dispensing Fee auto-sync
 * -------------------------------------------------------------
 * Rule: one $7.99 "Dispensing Fee" line item per DISTINCT prescription
 * product present in the cart, regardless of quantity of that product.
 *
 * How it works:
 * 1. window.RX_PRODUCT_IDS (set by snippet-rx-product-ids.liquid) tells us
 *    which product IDs are prescription items.
 * 2. We patch window.fetch so that ANY cart-mutating request
 *    (/cart/add.js, /cart/change.js, /cart/update.js, /cart/clear.js)
 *    triggers a reconciliation pass afterwards.
 * 3. Reconciliation reads the live cart, works out which distinct Rx
 *    products are present, compares that to the Dispensing Fee line
 *    items already in the cart (tracked via a line item property
 *    "_rx_product_id"), and adds/removes Dispensing Fee lines to match.
 * 4. Our OWN reconciliation requests are tagged so they don't trigger
 *    infinite reconciliation loops.
 * 5. After reconciling, we refresh the visible cart drawer / cart page
 *    sections so the customer sees the updated total immediately.
 */
(function () {
  var DISPENSING_FEE_VARIANT_ID = window.DISPENSING_FEE_VARIANT_ID;
  var RX_PRODUCT_IDS = (window.RX_PRODUCT_IDS || []).map(function (id) {
    return Number(id);
  });
  var RECONCILE_HEADER = "X-Dispensing-Fee-Sync";
  var isReconciling = false;
  var reconcilePromise = null;

  if (!DISPENSING_FEE_VARIANT_ID || RX_PRODUCT_IDS.length === 0) {
    // Nothing to do if the config snippet wasn't found on this page.
    return;
  }

  function isRxProductId(productId) {
    return RX_PRODUCT_IDS.indexOf(Number(productId)) !== -1;
  }

  function isDispensingFeeLine(line) {
    return Number(line.variant_id) === Number(DISPENSING_FEE_VARIANT_ID);
  }

  function fetchCart() {
    return fetch("/cart.js", { headers: { Accept: "application/json" } }).then(
      function (r) {
        return r.json();
      }
    );
  }

  function markedFetch(url, options) {
    options = options || {};
    options.headers = Object.assign({}, options.headers, {
      "Content-Type": "application/json",
      Accept: "application/json",
    });
    options.headers[RECONCILE_HEADER] = "1";
    return fetch(url, options);
  }

  function addDispensingFeeFor(rxLine) {
    return markedFetch("/cart/add.js", {
      method: "POST",
      body: JSON.stringify({
        id: DISPENSING_FEE_VARIANT_ID,
        quantity: 1,
        properties: {
          "Dispensing Fee For": rxLine.product_title + " - " + rxLine.variant_title,
          _rx_product_id: String(rxLine.product_id),
        },
      }),
    });
  }

  function removeDispensingFeeLine(line) {
    return markedFetch("/cart/change.js", {
      method: "POST",
      body: JSON.stringify({ id: line.key, quantity: 0 }),
    });
  }

  function refreshCartUI() {
    // Best-effort visual refresh for Dawn-based themes. Falls back to a
    // full reload of the cart page if we're already sitting on /cart.
    var sectionIds = [
      "cart-drawer",
      "cart-icon-bubble",
      "main-cart-items",
      "main-cart-footer",
    ];
    var params = sectionIds.join(",");
    fetch("/?sections=" + encodeURIComponent(params))
      .then(function (r) {
        return r.json();
      })
      .then(function (sections) {
        sectionIds.forEach(function (id) {
          var html = sections[id];
          if (!html) return;
          var el = document.getElementById(id);
          if (!el) return;
          var temp = document.createElement("div");
          temp.innerHTML = html;
          var replacement = temp.querySelector("#" + id);
          if (replacement) {
            el.innerHTML = replacement.innerHTML;
          }
        });
      })
      .catch(function () {
        // If section refresh fails and we're on the cart page itself,
        // just reload so totals are correct.
        if (window.location.pathname.indexOf("/cart") === 0) {
          window.location.reload();
        }
      });
  }

  function reconcile() {
    if (isReconciling) return reconcilePromise || Promise.resolve();
    isReconciling = true;

    reconcilePromise = fetchCart()
      .then(function (cart) {
        var rxLinesByProduct = {};
        var dispensingLines = [];

        cart.items.forEach(function (line) {
          if (isDispensingFeeLine(line)) {
            dispensingLines.push(line);
          } else if (isRxProductId(line.product_id)) {
            // Keep the first line we see for each distinct Rx product,
            // just need one representative line for its title.
            if (!rxLinesByProduct[line.product_id]) {
              rxLinesByProduct[line.product_id] = line;
            }
          }
        });

        var presentRxProductIds = Object.keys(rxLinesByProduct);

        var existingFeeProductIds = dispensingLines
          .map(function (line) {
            return line.properties && line.properties._rx_product_id;
          })
          .filter(Boolean);

        var toAdd = presentRxProductIds.filter(function (pid) {
          return existingFeeProductIds.indexOf(pid) === -1;
        });

        var toRemove = dispensingLines.filter(function (line) {
          var pid = line.properties && line.properties._rx_product_id;
          return !pid || presentRxProductIds.indexOf(pid) === -1;
        });

        var ops = [];
        toAdd.forEach(function (pid) {
          ops.push(addDispensingFeeFor(rxLinesByProduct[pid]));
        });
        toRemove.forEach(function (line) {
          ops.push(removeDispensingFeeLine(line));
        });

        if (ops.length === 0) {
          isReconciling = false;
          return;
        }

        return Promise.all(ops).then(function () {
          isReconciling = false;
          refreshCartUI();
        });
      })
      .catch(function () {
        isReconciling = false;
      });

    return reconcilePromise;
  }

  // ---- block checkout navigation until reconciliation is confirmed done ----
  var CHECKOUT_SELECTOR =
    'button[name="checkout"], input[name="checkout"], a[href^="/checkout"], a[href*="/checkout"]';

  document.addEventListener(
    "click",
    function (e) {
      var target = e.target.closest && e.target.closest(CHECKOUT_SELECTOR);
      if (!target || target.dataset.dispensingFeeChecked === "1") return;

      e.preventDefault();
      e.stopPropagation();

      reconcile().then(function () {
        target.dataset.dispensingFeeChecked = "1";
        if (target.tagName === "A") {
          window.location.href = target.href;
        } else {
          target.click();
        }
      });
    },
    true
  );

  // ---- patch fetch so any cart mutation triggers reconciliation ----
  var originalFetch = window.fetch;
  var CART_MUTATION_PATTERNS = [
    "/cart/add",
    "/cart/change.js",
    "/cart/update.js",
    "/cart/clear.js",
  ];

  window.fetch = function (input, init) {
    var url = typeof input === "string" ? input : input && input.url;
    var isCartMutation =
      url &&
      CART_MUTATION_PATTERNS.some(function (p) {
        return url.indexOf(p) !== -1;
      });
    var isOurOwnCall =
      init && init.headers && init.headers[RECONCILE_HEADER] === "1";

    var result = originalFetch.apply(this, arguments);

    if (isCartMutation && !isOurOwnCall) {
      result.then(function () {
        // slight delay so Shopify's own cart state is fully committed
        setTimeout(reconcile, 150);
      });
    }
    return result;
  };

  // Also reconcile once on page load, in case the cart already
  // contains Rx items without a matching fee (e.g. after a page refresh).
  document.addEventListener("DOMContentLoaded", function () {
    reconcile();
  });
})();
